import { AxiosError } from 'axios';
import { apiService } from '@shared/services/apiServices';
import contactRequestEndpoints from './contactRequestEndpoints';
import {
  handleContactRequestError,
  ContactRequestErrorCode,
  contactRequestErrorMessages,
} from '../errors';
import type {
  AssignContactRequestPayload,
  AssignContactRequestResponseData,
  AssignContactRequestResult,
} from '../types/assignContactRequestTypes';

/** Shown when a 409 indicates another admin already assigned the request first. */
export const ALREADY_ASSIGNED_ERROR =
  'This contact request has already been assigned by another admin';

/**
 * Assigns a contact request to an admin.
 *
 * Endpoint: `POST contact-request/contact-requests/{id}/assign`
 *
 * Only accepted while the request is `in_review` or `assigned`. A 409
 * response means another admin assigned it first — surfaced as `conflict: true`
 * so the caller can show {@link ALREADY_ASSIGNED_ERROR} and refresh.
 *
 * Access: SUPER_ADMIN | ADMIN
 */
export const assignContactRequest = async ({
  contactRequestId,
  assigneeId,
}: AssignContactRequestPayload): Promise<AssignContactRequestResult> => {
  const trimmedId = contactRequestId.trim();
  if (!trimmedId || !assigneeId) {
    return {
      success: false,
      error: contactRequestErrorMessages[ContactRequestErrorCode.UNKNOWN],
    };
  }

  try {
    const result = await apiService.postAPI(
      contactRequestEndpoints.ASSIGN_CONTACT_REQUEST(trimmedId),
      { assignee_id: assigneeId },
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        assignment?: AssignContactRequestResponseData;
      };
      return {
        success: true,
        ...(payload.assignment ? { data: payload.assignment } : {}),
      };
    }

    if (result.status === 409) {
      return { success: false, error: ALREADY_ASSIGNED_ERROR, conflict: true };
    }

    const { errorMessage } = handleContactRequestError(
      result.status as number | undefined,
      result.data,
      !!result.isNetworkError
    );
    return { success: false, error: errorMessage };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 409) {
        return {
          success: false,
          error: ALREADY_ASSIGNED_ERROR,
          conflict: true,
        };
      }
      const { errorMessage } = handleContactRequestError(
        error.response?.status,
        error.response?.data,
        !error.response && !!error.request
      );
      return { success: false, error: errorMessage };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : contactRequestErrorMessages[
              ContactRequestErrorCode.UNEXPECTED_ERROR
            ],
    };
  }
};
